import React, { useCallback, useMemo } from "react";
import styled from "styled-components";
import { deleteVal, getVal, setVal } from "oolib";

import { WidgetHeader } from "../../WidgetBarChart/WIDGET_ELEM_ConfigForm";
import { ConfigForm_SectionWrapper } from "../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import TooltipV2 from "@/components/UI/Tooltip";

import { TitleInputWithDefault } from "../../utils/TitleInputWithDefault";
import { CUSTOM_WIDGET_LABEL } from "../labels";

import { DATA_KEYS, TEMP_WIDGET_PROPS_KEY } from "../DATA_KEYS";
import { getTempWidget, getTempWidgetOption, useAddableTempWidgetOptions } from "../TEMP_WIDGETS";

const StyledSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

/**
 * The container's config panel: the widget header, the Temp Widget picker, and
 * then nothing of its own — everything below is the picked widget's own
 * ConfigForm, rendered inline so the panel reads as one form.
 */
export const WIDGET_ELEM_ConfigForm = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
}) => {
  const tempWidget = getVal(thisWidgetConfig, DATA_KEYS.tempWidget);
  const entry = getTempWidget(tempWidget);
  const selectedOption = getTempWidgetOption(tempWidget);

  /** The shared updateWidget boilerplate, same shape as the metric card's. */
  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      widgetConfig = updaterFn(widgetConfig);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  /**
   * Switching temp widget drops the previous one's props wholesale. They are
   * meaningless to the new widget, and keeping them would resurrect stale
   * config if you switched back and forth — the one real benefit of nesting
   * the slice (see ../DATA_KEYS.js).
   */
  const handleTempWidgetChange = useCallback((_id, option) => {
    updateWidget((w) => {
      if (getVal(w, DATA_KEYS.tempWidget) === option?.value) return w;
      w = option?.value
        ? setVal(w, DATA_KEYS.tempWidget, option.value)
        : deleteVal(w, DATA_KEYS.tempWidget);
      w = deleteVal(w, DATA_KEYS.tempWidgetProps);
      return w;
    });
  }, [updateWidget]);

  /**
   * What the picked widget's ConfigForm gets. Two tiers on purpose:
   *   props / updateProps  — all a simple temp widget ever needs, and it never
   *                          learns where its props actually live.
   *   widgetConfig / setConfig / underEditWidgetIdx / propsPath — the escape
   *                          hatch for anything that needs the WHOLE widget
   *                          config, e.g. <WidgetFilters configPath={...} />,
   *                          which already takes a configPath prop.
   */
  const tempWidgetFormProps = useMemo(() => ({
    props: getVal(thisWidgetConfig, DATA_KEYS.tempWidgetProps) || {},
    updateProps: (updaterFn) =>
      updateWidget((w) =>
        setVal(
          w,
          DATA_KEYS.tempWidgetProps,
          updaterFn(JSON.parse(JSON.stringify(getVal(w, DATA_KEYS.tempWidgetProps) || {})))
        )
      ),
    propsPath: DATA_KEYS.tempWidgetProps,
    propsKey: TEMP_WIDGET_PROPS_KEY,
    widgetConfig: thisWidgetConfig,
    setConfig,
    underEditWidgetIdx,
  }), [thisWidgetConfig, updateWidget, setConfig, underEditWidgetIdx]);

  const addableTempWidgetOptions = useAddableTempWidgetOptions();

  const TempWidgetConfigForm = entry?.ConfigForm;

  return (
    <StyledSectionsWrapper>
      <WidgetHeader
        title={selectedOption?.display || "Select a widget"}
        type={CUSTOM_WIDGET_LABEL}
        icon={selectedOption?.icon || "Flask"}
      />

      {/* Title first, like every other widget's Caption section. In widget-title
          mode TitleInputWithDefault owns the read AND the write of props.title —
          the same key the widget shell renders — and falls back to the picked
          widget's name via useWidgetTitle's WidgetTemp_SHI case. */}
      <ConfigForm_SectionWrapper icon="Selection" title="Title">
        <TooltipV2 text="Add a custom title for this widget" placement="right">
          <TitleInputWithDefault
            widgetConfig={thisWidgetConfig}
            widgetType="WidgetTemp_SHI"
            setConfig={setConfig}
            underEditWidgetIdx={underEditWidgetIdx}
            tagLabel="TITLE"
            placeholder="Update widget title"
          />
        </TooltipV2>
      </ConfigForm_SectionWrapper>

      <ConfigForm_SectionWrapper icon="Stack" title={CUSTOM_WIDGET_LABEL}>
        {addableTempWidgetOptions.length === 0 ? (
          <EmptyRegistryNote />
        ) : (
          <DataFieldInputSelector
            id={DATA_KEYS.tempWidget}
            options={addableTempWidgetOptions}
            value={selectedOption ? { ...selectedOption, dataType: CUSTOM_WIDGET_LABEL } : null}
            onChange={handleTempWidgetChange}
            placeholder="Select widget"
            placeholderIcon="Flask"
            trailingIcon="CaretRight"
            hideSearchHeader
          />
        )}
      </ConfigForm_SectionWrapper>

      {TempWidgetConfigForm && <TempWidgetConfigForm {...tempWidgetFormProps} />}
    </StyledSectionsWrapper>
  );
};

/** Only reachable if every temp widget has been deleted from the registry. */
const EmptyRegistryNote = () => (
  <div style={{ padding: "0.8rem 0", color: "var(--grey500)", fontSize: "1.2rem" }}>
    No temp widgets registered yet — add one in
    {" "}<code>tempWidgets/_EXPORTS.js</code>.
  </div>
);
